import React from 'react';
import {
    EnabledUrgencySignals,
    UrgencyHighlights,
    UrgencyHighlightType,
    UrgencyMessages,
    UrgencyMessageType,
    UrgencySignalsProps,
} from './types';

const UrgencySignalsContext = React.createContext<EnabledUrgencySignals | null>(
    null,
);

export const UrgencySignalsProvider: React.FC<{
    urgencySignals: UrgencySignalsProps;
}> = ({ urgencySignals, children }) => {
    return (
        <UrgencySignalsContext.Provider
            value={urgencySignalsToShow(urgencySignals)}
        >
            {children}
        </UrgencySignalsContext.Provider>
    );
};

export const useUrgencyHighlights = () => {
    const urgencySignals = React.useContext(UrgencySignalsContext);

    if (urgencySignals === null) {
        return {};
    }

    return urgencySignals?.highlights;
};

export const useUrgencyMessages = () => {
    const urgencySignals = React.useContext(UrgencySignalsContext);

    if (urgencySignals === null) {
        return {};
    }

    return urgencySignals?.messages;
};

function urgencySignalsToShow(
    urgencySignals: UrgencySignalsProps,
): EnabledUrgencySignals | null {
    const MAX_URGENCY_SIGNALS_TO_SHOW = 2;

    if (!urgencySignals) {
        return null;
    }

    const activeUrgencyMessage = pickActiveUrgencyMessageByPriority(
        urgencySignals.messages,
    );

    const activeUrgencyHighlights = pickActiveUrgencyHighlightsByPriority(
        urgencySignals.highlights,
        MAX_URGENCY_SIGNALS_TO_SHOW - Object.keys(activeUrgencyMessage).length,
    );

    return {
        messages: activeUrgencyMessage,
        highlights: activeUrgencyHighlights,
    };
}

function pickActiveUrgencyMessageByPriority(messages: UrgencyMessages) {
    let result: Partial<Record<UrgencyMessageType, boolean>> = {};

    if (messages[UrgencyMessageType.FewTickets]) {
        result = { [UrgencyMessageType.FewTickets]: true };
    } else if (messages[UrgencyMessageType.SalesEndSoon]) {
        result = { [UrgencyMessageType.SalesEndSoon]: true };
    }

    return result;
}

function pickActiveUrgencyHighlightsByPriority(
    highlights: UrgencyHighlights,
    slotsLeft: number,
) {
    const result: Partial<Record<UrgencyHighlightType, boolean>> = {};

    if (slotsLeft === 0) {
        return result;
    }

    const highlightsSortedByPriority = [
        UrgencyHighlightType.EarlyBird,
        UrgencyHighlightType.Popular,
        UrgencyHighlightType.New,
    ];

    highlightsSortedByPriority.map((highlightName) => {
        if (slotsLeft > 0 && highlights[highlightName]) {
            result[highlightName] = true;
            slotsLeft--;
        }
    });

    return result;
}
